import { useEffect, useState } from 'react';
import { db, auth } from '../FirebaseAuthConfig';
import { onSnapshot, collection, getDoc, doc } from 'firebase/firestore';

import Loader from './loader.js';

function Admin() {
    const [isLoading, setIsLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [users, setUsers] = useState([]);
    
    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [participationFilter, setParticipationFilter] = useState('all');
    const [minPoints, setMinPoints] = useState('');
    const [maxPoints, setMaxPoints] = useState('');

    // Check if current user is admin
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
            if (currentUser) {
                try {
                    // Check isAdmin on the user document
                    const userDoc = await getDoc(doc(db, `userCollection/${currentUser.uid}`));
                    if (userDoc.exists() && userDoc.data().isAdmin === true) {
                        setIsAdmin(true);
                    } else {
                        // Not admin, redirect
                        window.location.replace('/');
                    }
                } catch (error) {
                    console.error('Error checking admin status:', error);
                    window.location.replace('/');
                }
            } else {
                // Not logged in, redirect
                window.location.replace('/auth');
            }
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Fetch registered users data once admin is confirmed
    useEffect(() => {
        if (!isAdmin) return;

        // Get registered users
        const unsubRegistered = onSnapshot(
            collection(db, 'challengeCollection/everydayWarship/registeredUsers'),
            async (snapshot) => {
                const registeredUsers = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // For each registered user, fetch their user data and points
                const usersWithDetails = await Promise.all(
                    registeredUsers.map(async (regUser) => {
                        try {
                            // Get user data (displayName, email)
                            const userDoc = await getDoc(doc(db, `userCollection/${regUser.id}`));
                            const userData = userDoc.exists() ? userDoc.data() : {};

                            // Get points from pointsPool
                            const pointsDoc = await getDoc(
                                doc(db, `userCollection/${regUser.id}/challengeData/everydayWarship/pointsPool/master`)
                            );
                            const pointsData = pointsDoc.exists() ? pointsDoc.data() : {};

                            return {
                                id: regUser.id,
                                displayName: userData.displayName || 'Unknown',
                                email: userData.email || '',
                                phone: userData.phone || '',
                                participationType: regUser.participationType || '',
                                battlePartner: regUser.battlePartner || '',
                                points: pointsData.points || 0,
                                workPoints: pointsData.workPoints || 0,
                                abstainPoints: pointsData.abstainPoints || 0,
                                renewPoints: pointsData.renewPoints || 0,
                                bonusPoints: pointsData.bonusPoints || 0,
                                meps: pointsData.meps || 0,
                                steps: pointsData.steps || 0,
                            };
                        } catch (error) {
                            console.error(`Error fetching data for user ${regUser.id}:`, error);
                            return {
                                id: regUser.id,
                                displayName: 'Error loading',
                                email: '',
                                phone: '',
                                participationType: '',
                                points: 0,
                                workPoints: 0,
                                abstainPoints: 0,
                                renewPoints: 0,
                                bonusPoints: 0,
                                meps: 0,
                                steps: 0,
                            };
                        }
                    })
                );

                // Sort by points descending
                usersWithDetails.sort((a, b) => b.points - a.points);
                setUsers(usersWithDetails);
            },
            (error) => {
                console.error('Error fetching registered users:', error);
            }
        );

        return () => unsubRegistered();
    }, [isAdmin]);

    // Filter users based on all filters
    const filteredUsers = users.filter((user) => {
        // Search filter (name or email)
        const matchesSearch =
            user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());

        // Participation type filter
        const matchesParticipation =
            participationFilter === 'all' || user.participationType === participationFilter;

        // Points range filter
        const min = minPoints === '' ? -Infinity : parseInt(minPoints, 10);
        const max = maxPoints === '' ? Infinity : parseInt(maxPoints, 10);
        const matchesPoints = user.points >= min && user.points <= max;

        return matchesSearch && matchesParticipation && matchesPoints;
    });

    // Get participation type display
    const getParticipationType = (type) => {
        switch (type) {
            case 'battlePartner':
                return 'Battle Partner';
            case 'individual':
                return 'Individual';
            default:
                return type || '-';
        }
    };

    // Export to CSV
    const exportToCSV = () => {
        // CSV headers
        const headers = [
            'Rank',
            'Display Name',
            'Email',
            'Phone',
            'Participation Type',
            'Total Points',
            'Work Points',
            'Abstain Points',
            'Renew Points',
            'Bonus Points',
            'MEPs',
            'Steps',
        ];

        // CSV rows
        const rows = filteredUsers.map((user, index) => [
            index + 1,
            user.displayName,
            user.email,
            user.phone,
            getParticipationType(user.participationType),
            user.points,
            user.workPoints,
            user.abstainPoints,
            user.renewPoints,
            user.bonusPoints,
            user.meps,
            user.steps,
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map((row) =>
                row.map((cell) => {
                    // Escape cells that contain commas or quotes
                    const cellStr = String(cell);
                    if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                        return `"${cellStr.replace(/"/g, '""')}"`;
                    }
                    return cellStr;
                }).join(',')
            ),
        ].join('\n');

        // Create and download the file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `challenge_users_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setParticipationFilter('all');
        setMinPoints('');
        setMaxPoints('');
    };

    if (isLoading) {
        return (
            <div className="w-screen h-screen bg-neutral-100 dark:bg-neutral-900 flex items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (!isAdmin) {
        return null;
    }

    return (
        <div className="w-full min-h-screen bg-neutral-100 dark:bg-neutral-900 p-4 md:p-8">
            <div className="w-[90%] md:max-w-[75%] mx-auto">
                {/* Header */}
                <div className="flex flex-row justify-between items-start mb-6">
                    <div>
                        <h1 className="font-sans text-2xl md:text-3xl font-bold text-neutral-800 dark:text-white mb-2">
                            Admin Dashboard
                        </h1>
                        <p className="font-sans text-sm text-neutral-600 dark:text-neutral-400">
                            {filteredUsers.length} of {users.length} registered users
                        </p>
                    </div>
                    <button
                        onClick={exportToCSV}
                        className="h-10 px-4 border-none rounded-lg font-sans text-sm font-semibold text-white bg-[#FF671F] cursor-pointer"
                    >
                        Export CSV
                    </button>
                </div>

                {/* Filters */}
                <div className="mb-4 p-4 rounded-xl bg-white dark:bg-neutral-800 shadow">
                    <div className="flex flex-row flex-wrap gap-3 items-end">
                        {/* Search */}
                        <div className="flex flex-col gap-1">
                            <label className="font-sans text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                Search
                            </label>
                            <input
                                type="text"
                                placeholder="Name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-48 h-9 px-3 border border-neutral-200 dark:border-neutral-600 rounded-lg font-sans text-sm text-neutral-800 dark:text-white bg-neutral-50 dark:bg-neutral-700 focus:outline focus:outline-2 focus:outline-[#FF671F]"
                            />
                        </div>

                        {/* Participation Type Filter */}
                        <div className="flex flex-col gap-1">
                            <label className="font-sans text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                Participation Type
                            </label>
                            <select
                                value={participationFilter}
                                onChange={(e) => setParticipationFilter(e.target.value)}
                                className="w-40 h-9 px-3 border border-neutral-200 dark:border-neutral-600 rounded-lg font-sans text-sm text-neutral-800 dark:text-white bg-neutral-50 dark:bg-neutral-700 focus:outline focus:outline-2 focus:outline-[#FF671F]"
                            >
                                <option value="all">All Types</option>
                                <option value="battlePartner">Battle Partner</option>
                                <option value="individual">Individual</option>
                            </select>
                        </div>

                        {/* Min Points */}
                        <div className="flex flex-col gap-1">
                            <label className="font-sans text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                Min Points
                            </label>
                            <input
                                type="number"
                                placeholder="0"
                                value={minPoints}
                                onChange={(e) => setMinPoints(e.target.value)}
                                className="w-24 h-9 px-3 border border-neutral-200 dark:border-neutral-600 rounded-lg font-sans text-sm text-neutral-800 dark:text-white bg-neutral-50 dark:bg-neutral-700 focus:outline focus:outline-2 focus:outline-[#FF671F]"
                            />
                        </div>

                        {/* Max Points */}
                        <div className="flex flex-col gap-1">
                            <label className="font-sans text-xs font-medium text-neutral-500 dark:text-neutral-400">
                                Max Points
                            </label>
                            <input
                                type="number"
                                placeholder="∞"
                                value={maxPoints}
                                onChange={(e) => setMaxPoints(e.target.value)}
                                className="w-24 h-9 px-3 border border-neutral-200 dark:border-neutral-600 rounded-lg font-sans text-sm text-neutral-800 dark:text-white bg-neutral-50 dark:bg-neutral-700 focus:outline focus:outline-2 focus:outline-[#FF671F]"
                            />
                        </div>

                        {/* Clear Filters */}
                        <button
                            onClick={clearFilters}
                            className="h-9 px-3 border-none rounded-lg font-sans text-sm text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-600 cursor-pointer"
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {/* Table Container */}
                <div className="w-full overflow-x-auto rounded-xl bg-white dark:bg-neutral-800 shadow">
                    <table className="w-full min-w-[900px]">
                        <thead>
                            <tr className="border-b border-neutral-200 dark:border-neutral-700">
                                <th className="px-4 py-3 text-left font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    #
                                </th>
                                <th className="px-4 py-3 text-left font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    Name
                                </th>
                                <th className="px-4 py-3 text-left font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    Email
                                </th>
                                <th className="px-4 py-3 text-left font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    Phone
                                </th>
                                <th className="px-4 py-3 text-left font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    Type
                                </th>
                                <th className="px-4 py-3 text-right font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    Points
                                </th>
                                <th className="px-4 py-3 text-right font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    Work
                                </th>
                                <th className="px-4 py-3 text-right font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    Abstain
                                </th>
                                <th className="px-4 py-3 text-right font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    Renew
                                </th>
                                <th className="px-4 py-3 text-right font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    Bonus
                                </th>
                                <th className="px-4 py-3 text-right font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    MEPs
                                </th>
                                <th className="px-4 py-3 text-right font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    Steps
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((user, index) => (
                                <tr
                                    key={user.id}
                                    className="border-b border-neutral-100 dark:border-neutral-700 last:border-b-0"
                                >
                                    <td className="px-4 py-3 font-sans text-sm text-neutral-500 dark:text-neutral-400">
                                        {index + 1}
                                    </td>
                                    <td className="px-4 py-3 font-sans text-sm font-medium text-neutral-800 dark:text-white">
                                        {user.displayName}
                                    </td>
                                    <td className="px-4 py-3 font-sans text-sm text-neutral-600 dark:text-neutral-300">
                                        {user.email}
                                    </td>
                                    <td className="px-4 py-3 font-sans text-sm text-neutral-600 dark:text-neutral-300">
                                        {user.phone || '-'}
                                    </td>
                                    <td className="px-4 py-3 font-sans text-sm text-neutral-600 dark:text-neutral-300">
                                        {getParticipationType(user.participationType)}
                                    </td>
                                    <td className="px-4 py-3 font-sans text-sm font-bold text-[#FF671F] text-right">
                                        {user.points}
                                    </td>
                                    <td className="px-4 py-3 font-sans text-sm text-neutral-600 dark:text-neutral-300 text-right">
                                        {user.workPoints}
                                    </td>
                                    <td className="px-4 py-3 font-sans text-sm text-neutral-600 dark:text-neutral-300 text-right">
                                        {user.abstainPoints}
                                    </td>
                                    <td className="px-4 py-3 font-sans text-sm text-neutral-600 dark:text-neutral-300 text-right">
                                        {user.renewPoints}
                                    </td>
                                    <td className="px-4 py-3 font-sans text-sm text-neutral-600 dark:text-neutral-300 text-right">
                                        {user.bonusPoints}
                                    </td>
                                    <td className="px-4 py-3 font-sans text-sm text-neutral-600 dark:text-neutral-300 text-right">
                                        {user.meps.toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 font-sans text-sm text-neutral-600 dark:text-neutral-300 text-right">
                                        {user.steps.toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {filteredUsers.length === 0 && (
                        <div className="p-8 text-center">
                            <p className="font-sans text-sm text-neutral-500 dark:text-neutral-400">
                                No users found matching your filters.
                            </p>
                        </div>
                    )}
                </div>

                {/* Back to Home */}
                <div className="mt-6">
                    <button
                        onClick={() => window.location.replace('/')}
                        className="font-sans text-sm text-neutral-600 dark:text-neutral-400 border-none bg-transparent cursor-pointer"
                    >
                        ← Back to Home
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Admin;
